import type { ClaimWithRelations } from "../../api/use-claim";

export type TimelineNote = ClaimWithRelations["claimNotes"][number];
export type TimelineEvent = Omit<ClaimWithRelations["claimEvents"][number], "claimNotes"> & {
  claimNotes: TimelineNote[];
};

export type TimelineItem =
  | { type: "event"; data: TimelineEvent; date: Date }
  | { type: "note"; data: TimelineNote; date: Date };

/** Non-privileged users ([Upclaim roles, LAWYER, PLEADING_LAWYER]) only see PUBLIC notes. */
export function filterNotesByVisibility<T extends { visibility: "PRIVATE" | "PUBLIC" }>(
  notes: T[],
  canSeePrivateNotes: boolean,
): T[] {
  if (canSeePrivateNotes) {
    return notes;
  }
  return notes.filter((note) => note.visibility === "PUBLIC");
}

/**
 * Mirrors ClaimTimeline.vue: events (with their visibility-filtered nested notes)
 * merged with orphan notes, sorted newest-first (tie-break: newer createdAt first).
 */
export function buildTimelineItems(
  claim: Pick<ClaimWithRelations, "claimEvents" | "claimNotes"> | undefined,
  canSeePrivateNotes: boolean,
): TimelineItem[] {
  if (!claim) {
    return [];
  }

  const events: TimelineItem[] = (claim.claimEvents ?? []).map((event) => {
    const eventNotes = filterNotesByVisibility(
      (claim.claimNotes ?? [])
        .filter((note) => note.claimEventId === event.id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      canSeePrivateNotes,
    );

    return {
      type: "event",
      data: { ...event, claimNotes: eventNotes },
      date: new Date(event.eventDate),
    };
  });

  const orphanNotes: TimelineItem[] = filterNotesByVisibility(
    (claim.claimNotes ?? []).filter((note) => !note.claimEventId),
    canSeePrivateNotes,
  ).map((note) => ({
    type: "note",
    data: note,
    date: new Date(note.createdAt),
  }));

  return [...events, ...orphanNotes].sort((a, b) => {
    const dateDiff = b.date.getTime() - a.date.getTime();
    if (dateDiff !== 0) {
      return dateDiff;
    }
    return new Date(b.data.createdAt).getTime() - new Date(a.data.createdAt).getTime();
  });
}

export type TaskStatus = "completed" | "rejected" | "overdue" | "pending";

/** Mirrors ClaimNoteElement.vue task status derivation. */
export function deriveTaskStatus(
  task: { completedAt?: Date | string | null; rejectedAt?: Date | string | null; dueDate: Date | string },
  now: Date = new Date(),
): TaskStatus {
  if (task.completedAt) {
    return "completed";
  }
  if (task.rejectedAt) {
    return "rejected";
  }
  if (new Date(task.dueDate) < now) {
    return "overdue";
  }
  return "pending";
}

/** @mention pattern shared by the editor (extraction) and the note element (highlighting). */
export const MENTION_REGEX = /@([A-Za-zÀ-ÿ\s]+?)(?=\s|$|[,;.:!?])/g;

/**
 * Matches @mentions against the admin list by fullName (case-insensitive).
 * Scans for each known name directly instead of the display regex — the Vue
 * regex stopped at the first space, so multi-word names never matched.
 */
export function extractMentionAdminIds(
  content: string,
  admins: { id: string; fullName?: string | null }[],
): string[] {
  const haystack = content.toLowerCase();
  const mentionedAdminIds: string[] = [];

  for (const admin of admins) {
    const fullName = admin.fullName?.trim().toLowerCase();
    if (!fullName) {
      continue;
    }
    if (haystack.includes(`@${fullName}`) && !mentionedAdminIds.includes(admin.id)) {
      mentionedAdminIds.push(admin.id);
    }
  }

  return mentionedAdminIds;
}

export type MentionSegment = { type: "text" | "mention"; value: string };

/** Splits note content into text/mention segments so mentions can be rendered as React elements. */
export function splitMentionSegments(content: string): MentionSegment[] {
  const segments: MentionSegment[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(MENTION_REGEX)) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      segments.push({ type: "text", value: content.slice(lastIndex, matchIndex) });
    }
    segments.push({ type: "mention", value: match[1] });
    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ type: "text", value: content.slice(lastIndex) });
  }

  return segments;
}
